/**
 * Travel Location Creation/Edit Modal Component
 * Coláiste Lurgan Management System - Travel Location CRUD modal
 */

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../hooks/use-toast';

// UI Components
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../ui/form';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';

// Icons
import {
  MapPin,
  Loader2,
  CheckCircle,
} from 'lucide-react';

// Hooks
import { useCreateTravelLocation, useUpdateTravelLocation } from '../../hooks/useTravelLocations';

// Validation Schema
const travelLocationSchema = z.object({
  name: z.string()
    .min(2, 'Location name must be at least 2 characters')
    .max(100, 'Location name must not exceed 100 characters'),

  address: z.string()
    .max(500, 'Address must not exceed 500 characters')
    .optional()
    .or(z.literal('')),

  is_active: z.boolean(),
});

export function TravelLocationModal({
  isOpen,
  onClose,
  travelLocation = null, // If provided, this is an edit modal
  onSuccess
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditing = !!travelLocation;

  // Mutations
  const createTravelLocationMutation = useCreateTravelLocation();
  const updateTravelLocationMutation = useUpdateTravelLocation();

  // Form setup
  const form = useForm({
    resolver: zodResolver(travelLocationSchema),
    defaultValues: {
      name: travelLocation?.name || '',
      address: travelLocation?.address || '',
      is_active: travelLocation?.is_active ?? true,
    },
  });

  // Reset form when travelLocation prop changes (for editing)
  useEffect(() => {
    if (travelLocation) {
      form.reset({
        name: travelLocation.name || '',
        address: travelLocation.address || '',
        is_active: travelLocation.is_active ?? true,
      });
    } else {
      // Reset to empty form for new travel location
      form.reset({
        name: '',
        address: '',
        is_active: true,
      });
    }
  }, [travelLocation, form]);

  const onSubmit = async (data) => {
    setIsSubmitting(true);

    try {
      // Prepare travel location data
      const travelLocationData = {
        ...data,
        address: data.address || '', // Convert undefined to empty string
      };

      if (isEditing) {
        await updateTravelLocationMutation.mutateAsync({
          id: travelLocation.uuid,
          ...travelLocationData,
        });
      } else {
        await createTravelLocationMutation.mutateAsync(travelLocationData);
      }

      // Success
      onSuccess?.(travelLocationData, isEditing);
      onClose();

      // Reset form for next use
      if (!isEditing) {
        form.reset();
      }

    } catch (error) {
      console.error('Travel location submission error:', error);

      // Extract error message from API response
      let errorMessage = t('travelLocations.error.saveFailed', 'Failed to save travel location.');

      if (error.response?.data) {
        // Handle field-specific errors
        if (error.response.data.name) {
          errorMessage = Array.isArray(error.response.data.name)
            ? error.response.data.name[0]
            : error.response.data.name;
        } else if (error.response.data.address) {
          errorMessage = Array.isArray(error.response.data.address)
            ? error.response.data.address[0]
            : error.response.data.address;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.non_field_errors) {
          errorMessage = Array.isArray(error.response.data.non_field_errors)
            ? error.response.data.non_field_errors[0]
            : error.response.data.non_field_errors;
        }
      }

      toast({
        title: t('common.error', 'Error'),
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
      // Reset form when closing
      if (!isEditing) {
        form.reset();
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            {isEditing
              ? t('travelLocations.form.edit', 'Edit Travel Location')
              : t('travelLocations.form.create', 'Add Travel Location')
            }
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? t('travelLocations.form.editDescription', 'Update the travel location details.')
              : t('travelLocations.form.createDescription', 'Add a new pickup/drop-off location for students.')
            }
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Location Name */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('travelLocations.form.name', 'Location Name')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('travelLocations.form.namePlaceholder', 'e.g., Dublin Airport')}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    {t('travelLocations.form.nameHint', 'Clear, recognizable name for the pickup/drop-off location')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Address */}
            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('travelLocations.form.address', 'Address (Optional)')}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t('travelLocations.form.addressPlaceholder', 'Detailed address or meeting point description...')}
                      className="min-h-[80px]"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    {t('travelLocations.form.addressHint', 'Detailed directions or meeting point information')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Active Status */}
            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {t('travelLocations.form.active', 'Active Location')}
                    </FormLabel>
                    <FormDescription>
                      {t('travelLocations.form.activeHint', 'Whether this location is available for student selection')}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {/* Submit Buttons */}
            <div className="flex justify-end space-x-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="gap-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {isEditing
                      ? t('common.updating', 'Updating...')
                      : t('common.creating', 'Creating...')
                    }
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    {isEditing
                      ? t('common.update', 'Update Location')
                      : t('common.create', 'Create Location')
                    }
                  </>
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export default TravelLocationModal;